import type { AudienceErrors, AudienceValues } from './audience-values'

/**
 * A route prefix / locale list typed as free text: one entry per line, or comma
 * separated, whichever the operator reaches for. Blank entries are dropped so a
 * trailing newline never becomes an empty prefix — which on the BE would match
 * every route and silently widen the campaign.
 */
export const parseList = (raw: string): string[] =>
  raw
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')

/** Inverse of `parseList` for prefilling the textarea from a stored list. */
export const formatList = (values: string[] | undefined): string => (values ?? []).join('\n')

/**
 * The backend caps one section's audience fields with. Each section passes its
 * own, because each section's endpoint declares them.
 */
export type AudienceLimits = {
  requiredFlagMaxLength: number
  routePrefixMaxLength: number
  accountAgeMaxDays: number
  /** The most entries a route or locale list may hold; `undefined` = no cap. */
  listMaxItems?: number
}

// Matches what the customer app stores as its language setting: a bare language
// code, optionally regioned. Anything else targets a locale nobody runs and the
// campaign never appears.
const LOCALE_PATTERN = /^[a-z]{2}(?:-[A-Za-z0-9]{2,8})?$/

const isBlank = (value: string): boolean => value.trim() === ''

const tooManyMessage = (count: number, noun: string, max: number | undefined): string | null =>
  max !== undefined && count > max ? `List at most ${String(max)} ${noun}.` : null

/**
 * Route targeting is a `startsWith` match on the BE, so a prefix that does not
 * begin with `/` can never match anything and the campaign shows nowhere — the
 * failure mode is silence, which is why this is an error and not a hint.
 */
const validatePrefixes = (prefixes: string[], limits: AudienceLimits): string | null => {
  const invalid = prefixes.find((prefix) => !prefix.startsWith('/'))
  if (invalid !== undefined) {
    return `Every route prefix must start with "/" — got "${invalid}".`
  }

  const tooLong = prefixes.find((prefix) => prefix.length > limits.routePrefixMaxLength)
  if (tooLong !== undefined) {
    return `Route prefixes must be at most ${String(limits.routePrefixMaxLength)} characters.`
  }

  return tooManyMessage(prefixes.length, 'route prefixes', limits.listMaxItems)
}

const validateLocales = (locales: string[], limits: AudienceLimits): string | null => {
  const invalid = locales.find((locale) => !LOCALE_PATTERN.test(locale))
  if (invalid !== undefined) {
    return `"${invalid}" is not a locale code — use e.g. en, ru, pt-BR.`
  }

  return tooManyMessage(locales.length, 'locales', limits.listMaxItems)
}

/**
 * One account-age bound, recording its own error and reporting the number back
 * for the min-vs-max comparison.
 *
 * `null` covers both "blank" and "already rejected" on purpose: the cross-field
 * check needs two good numbers, and a bound that is not a whole day should be
 * reported as malformed once rather than also as being on the wrong side of the
 * other bound.
 */
const parseAgeBound = (
  errors: AudienceErrors,
  field: 'maxAccountAgeDays' | 'minAccountAgeDays',
  value: string,
  maxDays: number,
): number | null => {
  const raw = value.trim()
  if (isBlank(raw)) {
    return null
  }

  const parsed = Number(raw)
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > maxDays) {
    errors[field] = `Account age must be a whole number of days between 0 and ${String(maxDays)}.`

    return null
  }

  return parsed
}

/**
 * Every rule the backend applies to the audience fields, shared by every
 * targeted section (design spec §9.1). It returns only the fields that hold a
 * message: a present-but-undefined key would count as an error to a caller that
 * decides validity by its key count.
 */
export const validateAudience = (
  values: AudienceValues,
  limits: AudienceLimits,
): AudienceErrors => {
  const errors: AudienceErrors = {}

  if (values.requiredFlag.length > limits.requiredFlagMaxLength) {
    errors.requiredFlag = `Required flag must be at most ${String(limits.requiredFlagMaxLength)} characters.`
  }

  const prefixError = validatePrefixes(parseList(values.routePrefixes), limits)
  if (prefixError !== null) {
    errors.routePrefixes = prefixError
  }
  const excludeError = validatePrefixes(parseList(values.routeExcludePrefixes), limits)
  if (excludeError !== null) {
    errors.routeExcludePrefixes = excludeError
  }

  const localeError = validateLocales(parseList(values.locales), limits)
  if (localeError !== null) {
    errors.locales = localeError
  }

  if (values.audiencePlans.length > 0 && values.audience !== 'paid') {
    errors.audience = 'Select Paid accounts only to target plans.'
  }
  if (values.audience === 'all' && values.audienceInverted) {
    errors.audience = 'Select a specific audience before excluding it.'
  }

  // Both bounds are inclusive on the BE, so an equal pair is a real one-day
  // window rather than an empty one — only a strict `>` is wrong.
  const minAge = parseAgeBound(
    errors,
    'minAccountAgeDays',
    values.minAccountAgeDays,
    limits.accountAgeMaxDays,
  )
  const maxAge = parseAgeBound(
    errors,
    'maxAccountAgeDays',
    values.maxAccountAgeDays,
    limits.accountAgeMaxDays,
  )
  if (minAge !== null && maxAge !== null && minAge > maxAge) {
    errors.minAccountAgeDays = 'The minimum account age must not exceed the maximum.'
  }

  return errors
}
